import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { AlertCircle, LayoutGrid, Loader2, SearchX } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import SectionHeader from '@/components/SectionHeader';
import { getCategories, getProducts } from '@/lib/api';
import type { Category, Product } from '@/types';

const FALLBACK_CATEGORIES: Category[] = [
  {
    id: 'cat-metabolic',
    slug: 'metabolic',
    familyNumber: 'Family 01',
    title: 'Metabolic',
    description: 'Weight regulation and GLP-pathway compounds for body-composition protocols.',
  },
  {
    id: 'cat-tissue-repair',
    slug: 'tissue-repair',
    familyNumber: 'Family 02',
    title: 'Tissue Repair',
    description: 'Healing and regenerative peptides for joint, tendon and gut recovery.',
  },
  {
    id: 'cat-gh-axis',
    slug: 'gh-axis',
    familyNumber: 'Family 03',
    title: 'GH Axis',
    description: 'Growth-hormone secretagogues and pulse stacks for recovery and lean composition.',
  },
];

function productCover(product: Product): string | undefined {
  return product.image;
}

interface FilterChipProps {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}

function FilterChip({ active, onClick, children, icon }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.06em] transition-colors ${
        active
          ? 'border-rp-forest bg-rp-forest text-rp-paper'
          : 'border-rp-line bg-rp-paper text-rp-moss hover:border-rp-forest hover:text-rp-forest'
      }`}
    >
      {icon}
      {children}
    </button>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col rounded-2xl border border-rp-line bg-rp-bone p-4"
        >
          <div className="aspect-square animate-pulse rounded-xl bg-rp-paper" />
          <div className="mt-4 flex flex-1 flex-col">
            <div className="flex gap-1.5">
              <div className="h-5 w-16 animate-pulse rounded-full bg-rp-paper" />
              <div className="h-5 w-14 animate-pulse rounded-full bg-rp-paper" />
            </div>
            <div className="mt-3 h-5 w-3/4 animate-pulse rounded bg-rp-paper" />
            <div className="mt-2 h-3 w-1/3 animate-pulse rounded bg-rp-paper" />
            <div className="mt-auto flex items-center justify-between pt-4">
              <div className="h-5 w-16 animate-pulse rounded bg-rp-paper" />
              <div className="flex gap-2">
                <div className="h-9 w-16 animate-pulse rounded-full bg-rp-paper" />
                <div className="h-9 w-9 animate-pulse rounded-full bg-rp-paper" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message }: { message: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center rounded-2xl border border-rp-line bg-rp-bone py-16 text-center"
    >
      <AlertCircle size={40} className="text-rp-warn" />
      <p className="mt-4 text-base font-semibold text-rp-ink">Couldn’t load products</p>
      <p className="mt-1 max-w-xs text-sm text-rp-moss">{message}</p>
    </motion.div>
  );
}

function EmptyState({ category }: { category: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center rounded-2xl border border-rp-line bg-rp-bone py-16 text-center"
    >
      <SearchX size={40} className="text-rp-moss" />
      <p className="mt-4 text-base font-semibold text-rp-ink">
        No products in {category}
      </p>
      <p className="mt-1 max-w-xs text-sm text-rp-moss">
        Try switching to another category or browse all products.
      </p>
    </motion.div>
  );
}

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = (searchParams.get('category') ?? '').toLowerCase();

  const {
    data: apiCategories = [],
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useQuery<Category[]>({
    queryKey: ['categories'],
    queryFn: getCategories,
    staleTime: 5 * 60 * 1000,
  });

  const categoryList = useMemo(
    () => (apiCategories.length ? apiCategories : FALLBACK_CATEGORIES),
    [apiCategories]
  );

  const {
    data: products = [],
    isLoading: productsLoading,
    error: productsError,
  } = useQuery<Product[]>({
    queryKey: ['products', activeCategory],
    queryFn: () => getProducts(activeCategory || undefined),
  });

  const enrichedProducts = useMemo(
    () => products.map((p) => ({ ...p, image: productCover(p) })),
    [products]
  );

  const activeLabel = useMemo(() => {
    if (!activeCategory) return 'All products';
    const cat = categoryList.find((c) => c.slug === activeCategory);
    return cat?.title ?? activeCategory;
  }, [activeCategory, categoryList]);

  const setCategory = (slug: string) => {
    if (!slug) {
      setSearchParams({});
    } else {
      setSearchParams({ category: slug });
    }
  };

  const errorMessage =
    productsError instanceof Error
      ? productsError.message
      : categoriesError instanceof Error
        ? categoriesError.message
        : 'Something went wrong. Please try again.';

  return (
    <section className="bg-rp-paper py-10 md:py-20">
      <div className="rp-container">
        <SectionHeader
          eyebrow="Shop"
          title="All products"
          description="Browse the AURA research peptide catalogue. Every vial is UAE-sourced with COA, batch traceability and research-use compliance."
          align="left"
          right={
            <span className="rp-mono-mini text-rp-moss">
              {productsLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                `${enrichedProducts.length} product${enrichedProducts.length !== 1 ? 's' : ''}`
              )}
            </span>
          }
        />

        <div className="mb-8 flex flex-nowrap items-center gap-2 overflow-x-auto pb-2 md:flex-wrap">
          <FilterChip
            active={!activeCategory}
            onClick={() => setCategory('')}
            icon={<LayoutGrid size={12} />}
          >
            All
          </FilterChip>
          {categoriesLoading
            ? FALLBACK_CATEGORIES.map((cat) => (
                <div
                  key={cat.slug}
                  className="h-8 w-24 animate-pulse rounded-full bg-rp-bone"
                />
              ))
            : categoryList.map((cat) => (
                <FilterChip
                  key={cat.id}
                  active={activeCategory === cat.slug}
                  onClick={() => setCategory(cat.slug)}
                >
                  {cat.familyNumber ? `${cat.familyNumber} · ` : ''}
                  {cat.title}
                </FilterChip>
              ))}
        </div>

        {productsError && <ErrorState message={errorMessage} />}

        {productsLoading && <SkeletonGrid />}

        {!productsLoading && !productsError && enrichedProducts.length === 0 && (
          <EmptyState category={activeLabel} />
        )}

        {!productsLoading && !productsError && enrichedProducts.length > 0 && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: { staggerChildren: 0.06 },
              },
            }}
            className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {enrichedProducts.map((product) => (
              <motion.div
                key={product.id}
                variants={{
                  hidden: { opacity: 0, y: 24 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
                }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}
